import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';

import { toaster } from '../components/ui/toaster';
import { fetchCurrentUser, logoutUser } from '../api/authApi';
import { globalLogoutRef } from '../api/client';

export interface UserInfo {
  id: string;
  customer_id: string;
  role: string;
  sites: string[];
  /** Active site preference from auth-wizard (`site` cookie), when present. */
  site?: string;
  username?: string;
}

interface AuthContextValue {
  user: UserInfo | null;
  isAuthenticated: boolean;
  hydrated: boolean;
  loading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [loading, setLoading] = useState(false);

  const logout = async () => {
    setLoading(true);
    try {
      await logoutUser();
    } catch (err) {
      toaster.create({
        title: 'Logout failed',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    // Register global logout for axios
    globalLogoutRef.fn = logout;
    const hydrate = async () => {
      try {
        const data = await fetchCurrentUser();
        setUser(data);
        setIsAuthenticated(true);
      } catch (_err) {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setHydrated(true);
      }
    };
    hydrate();
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      hydrated,
      loading,
      logout,
    }),
    [user, isAuthenticated, hydrated, loading]
  );

  return React.createElement(AuthContext.Provider, { value }, children);
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export default useAuth;
